import React, { useCallback, useMemo } from 'react';

import { Alert, Box, LinearProgress } from '@mui/material';
import { GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';

import { useGetPropertyRelations } from '~/hooks/apis/vendors/usePropertyRelations';

const renderConcatenatedCell = (codeField: string | null, nameField: string | null): string =>
  `${codeField ?? ''} ${nameField && codeField ? ' : ' : ''} ${nameField ?? ''}`;

const commonColumnProps = {
  sortable: true,
  disableColumnMenu: true,
};

const PAGE_SIZE = 200;

const nameColumns: Array<GridColDef> = [
  {
    field: 'property_name',
    headerName: 'Property Name',
    flex: 1,
    display: 'flex',
    ...commonColumnProps,
  },
  {
    field: 'room_type_name',
    headerName: 'Room Type',
    renderCell: ({ row }: GridRenderCellParams) => renderConcatenatedCell(row.room_type_code, row.room_type_name),
    flex: 1.3,
    display: 'flex',
    ...commonColumnProps,
  },
  {
    field: 'room_rate_id',
    headerName: 'Room Rate',
    flex: 1,
    display: 'flex',
    ...commonColumnProps,
  },
  {
    field: 'rate_plan_name',
    headerName: 'Rate Plan',
    renderCell: ({ row }: GridRenderCellParams) => renderConcatenatedCell(row.rate_plan_code, row.rate_plan_name),
    flex: 1.3,
    display: 'flex',
    ...commonColumnProps,
  },
];

interface Props {
  searchId: string;
  propertyId?: string;
  onCellClick?: (params: GridCellParams) => void;
}

function PropertyRelationsSearchResults({ searchId, propertyId, onCellClick }: Props) {
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, error } = useGetPropertyRelations(
    searchId,
    PAGE_SIZE,
    propertyId,
  );

  const rows = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    return data.pages.flatMap((page) => page.rows);
  }, [data?.pages]);

  const totalRowCount = data?.pages[0]?.total ?? 0;

  const handleRowsScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (error) {
    return <Alert severity="error">Error fetching property relations: {error.message}</Alert>;
  }

  return (
    <Box sx={{ height: 'calc(100vh - 250px)', minHeight: 400 }}>
      <DataGridPro
        rows={rows}
        columns={nameColumns}
        loading={isLoading}
        getRowId={(row) => row.row_index}
        onCellClick={onCellClick}
        rowCount={totalRowCount}
        onRowsScrollEnd={handleRowsScrollEnd}
        scrollEndThreshold={200}
        hideFooterPagination
        disableColumnFilter
      />
      {isFetchingNextPage && <LinearProgress />}
    </Box>
  );
}

export default PropertyRelationsSearchResults;
